/**
 * Private Segment HTTP client for the SDK.
 *
 * The SDK ships as a single IIFE loader that must stay tiny, so instead of
 * bundling `@segment/analytics-next` (far too heavy; IIFE can't code-split)
 * or loading analytics.js onto a global the host page also owns, this client
 * POSTs each event directly to Segment's tracking API—the same endpoints
 * analytics.js delivers to. Transport mirrors the hand-rolled PostHog capture
 * in `analytics.ts`: `navigator.sendBeacon` first, `fetch` with `keepalive`
 * as the fallback, failures swallowed—analytics must never break the SDK or
 * the host page.
 *
 * Isolation is the point: every event routes to the SDK's own Segment source
 * under Ekho's write key, and the SDK never reads or writes any host-page
 * analytics global, so a dealer's own Segment instance (identity chain,
 * middleware, destinations) is untouched.
 *
 * Identity: every call carries the Ekho-minted `ekho_aid` cookie as
 * `anonymousId`, read fresh per event, so Segment events share identity with
 * the PostHog events the SDK dual-writes and with
 * `MarketingAttribution.anonymousId` downstream.
 */

import { isBotRuntime } from '@ekhodealer/ekho-common/analytics/bot-detection';

import type { Logger } from '../loader/diagnostics';

import { captureAnonymousId } from './identity';

const SEGMENT_API_HOST = 'https://api.segment.io/v1';

/** Tracking-API path segment for each client method. */
const SegmentEndpoint = {
  Track: 't',
  Page: 'p',
  Identify: 'i',
  Group: 'g',
  Alias: 'a',
} as const;
type SegmentEndpoint = (typeof SegmentEndpoint)[keyof typeof SegmentEndpoint];

export interface SegmentClient {
  track: (event: string, properties?: Record<string, unknown>) => void;
  page: (name?: string, properties?: Record<string, unknown>) => void;
  identify: (userId: string, traits?: Record<string, unknown>) => void;
  group: (groupId: string, traits?: Record<string, unknown>) => void;
  alias: (userId: string, previousId?: string) => void;
}

export interface CreateSegmentClientOptions {
  writeKey: string;
  enabled: boolean;
  log: Logger;
}

function mintMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Pre-randomUUID browser fallback. Segment only needs per-source uniqueness
  // inside its dedupe window, so this entropy is plenty.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * analytics.js-shaped context so downstream tooling sees the familiar fields.
 *
 * `userAgentData` mirrors what analytics.js sends and is what makes automation
 * auditable in the warehouse: a headless Chromium names itself in
 * `brands` while its user-agent string claims an ordinary Chrome build, so
 * without this field the largest class of automation is invisible downstream
 * even though the client declares itself. Omitted entirely on browsers that do
 * not implement it, rather than sent as null.
 */
function buildContext(): Record<string, unknown> {
  const userAgentData = (navigator as Navigator & { userAgentData?: unknown }).userAgentData;
  return {
    page: {
      url: window.location.href,
      path: window.location.pathname,
      title: document.title,
      referrer: document.referrer,
      search: window.location.search,
    },
    userAgent: navigator.userAgent,
    ...(userAgentData ? { userAgentData } : {}),
    locale: navigator.language,
    library: { name: 'ekho-sdk' },
  };
}

function postToSegment(endpoint: SegmentEndpoint, body: Record<string, unknown>): void {
  const url = `${SEGMENT_API_HOST}/${endpoint}`;
  const payload = JSON.stringify(body);
  // text/plain keeps the cross-origin POST a CORS "simple request" — beacons
  // cannot run a preflight, and a non-safelisted content type can cause
  // sendBeacon to report success while the browser withholds delivery.
  // Segment's own browser clients ship JSON bodies as text/plain for the same
  // reason; the tracking API parses the body regardless of content type.
  if (typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([payload], { type: 'text/plain' });
    if (navigator.sendBeacon(url, blob)) return;
  }
  void fetch(url, {
    method: 'POST',
    body: payload,
    headers: { ['Content-Type']: 'text/plain' },
    keepalive: true,
  }).catch(() => {
    // Delivery failures are non-fatal and never surface to the host page.
  });
}

/**
 * Create the SDK's private Segment client. Returns `null` when running
 * without a DOM (SSR), when Segment is config-disabled, when the write
 * key is missing, or when the runtime is automation—callers treat a `null`
 * client as "Segment absent" and skip the destination entirely.
 *
 * Automation is refused here rather than per event because Segment prices per
 * tracked user: the SDK emits on every host page view, so one crawler walking a
 * dealer's catalog mints a billable identity per page. PostHog still receives
 * these events, so the traffic stays measurable without being billable.
 */
export function createSegmentClient(options: CreateSegmentClientOptions): SegmentClient | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (!options.enabled || !options.writeKey) {
    options.log('segment disabled or missing write key; skipping segment client');
    return null;
  }
  if (isBotRuntime()) {
    options.log('automation runtime detected; skipping segment client');
    return null;
  }
  const { writeKey } = options;

  const send = (endpoint: SegmentEndpoint, fields: Record<string, unknown>): void => {
    postToSegment(endpoint, {
      writeKey,
      // Read per call so every event carries the current `ekho_aid` value and
      // the read refreshes the cookie's two-year sliding expiry.
      anonymousId: captureAnonymousId(),
      context: buildContext(),
      timestamp: new Date().toISOString(),
      messageId: mintMessageId(),
      ...fields,
    });
  };

  return {
    track: (event, properties = {}): void => send(SegmentEndpoint.Track, { event, properties }),
    page: (name, properties = {}): void => send(SegmentEndpoint.Page, { name, properties }),
    identify: (userId, traits = {}): void => send(SegmentEndpoint.Identify, { userId, traits }),
    group: (groupId, traits = {}): void => send(SegmentEndpoint.Group, { groupId, traits }),
    alias: (userId, previousId): void =>
      send(SegmentEndpoint.Alias, { userId, previousId: previousId ?? captureAnonymousId() }),
  };
}
